import { BarChart2, Ban, EyeOff } from 'lucide-react';
import { useState } from 'react';
import AppModal from '../common/AppModal';
import {
  DeactivationReason,
  DEACTIVATION_REASON_OTHER_TEXT_MAX_LENGTH,
} from '../../types/pool';

interface DeactivatePoolModalProps {
  open: boolean;
  poolName: string;
  onClose: () => void;
  onConfirm: (reason: DeactivationReason, reasonText?: string) => Promise<void>;
}

const REASON_OPTIONS: { value: DeactivationReason; label: string }[] = [
  { value: DeactivationReason.CHURNED, label: 'Customer cancelled' },
  { value: DeactivationReason.SEASONAL_PAUSE, label: 'Seasonal pause' },
  { value: DeactivationReason.CONTRACT_ENDED, label: 'Contract ended' },
  { value: DeactivationReason.OTHER, label: 'Other' },
];

// Client-side maxLength is a UX nicety only - the server rejects over-length text, it doesn't truncate.
const DeactivatePoolModal = ({
  open,
  poolName,
  onClose,
  onConfirm,
}: DeactivatePoolModalProps) => {
  const [reason, setReason] = useState<DeactivationReason | null>(null);
  const [reasonText, setReasonText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (submitting) return;
    setReason(null);
    setReasonText('');
    setError(null);
    onClose();
  };

  const isOtherReason = reason === DeactivationReason.OTHER;
  const canSubmit =
    reason !== null && (!isOtherReason || reasonText.trim().length > 0);

  const handleConfirm = async () => {
    if (!reason || !canSubmit) return;

    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(reason, isOtherReason ? reasonText.trim() : undefined);
      setReason(null);
      setReasonText('');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to set pool to inactive.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppModal open={open} onClose={handleClose}>
      <div className="mb-4 text-center text-xl font-extrabold leading-tight text-dark">
        Set “{poolName}” to inactive?
      </div>

      <div className="text-sm font-medium text-dark">
        While inactive, this pool:
      </div>
      <ul className="my-2 flex flex-col gap-1.5">
        <li className="flex items-start gap-2 text-[13.5px] leading-snug text-light">
          <EyeOff className="mt-0.5 h-[15px] w-[15px] flex-shrink-0 text-primary" />
          Is hidden from the team&rsquo;s active list
        </li>
        <li className="flex items-start gap-2 text-[13.5px] leading-snug text-light">
          <Ban className="mt-0.5 h-[15px] w-[15px] flex-shrink-0 text-primary" />
          Can&rsquo;t take new services or repairs
        </li>
        <li className="flex items-start gap-2 text-[13.5px] leading-snug text-light">
          <BarChart2 className="mt-0.5 h-[15px] w-[15px] flex-shrink-0 text-primary" />
          Is excluded from financial reports
        </li>
      </ul>
      <div className="mt-1 text-[13px] text-light">
        All past data is kept, and you can reactivate any time.
      </div>

      <div className="mt-5">
        <div className="mb-3 text-sm font-bold text-dark">
          Reason for deactivation
        </div>
        <div className="flex flex-col gap-1">
          {REASON_OPTIONS.map((option) => {
            const selected = reason === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setReason(option.value)}
                className={`flex items-center gap-2.5 rounded-xl border-[1.5px] px-3 py-2.5 text-left text-sm font-medium text-dark transition-colors ${
                  selected
                    ? 'border-primary bg-cloud'
                    : 'border-grey3 hover:border-primary'
                }`}
              >
                <span
                  className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${
                    selected ? 'border-primary' : 'border-grey10'
                  }`}
                >
                  {selected && (
                    <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                  )}
                </span>
                {option.label}
              </button>
            );
          })}
        </div>

        {isOtherReason && (
          <div className="mt-2.5">
            <input
              type="text"
              autoFocus
              value={reasonText}
              onChange={(e) =>
                setReasonText(
                  e.target.value.slice(
                    0,
                    DEACTIVATION_REASON_OTHER_TEXT_MAX_LENGTH
                  )
                )
              }
              maxLength={DEACTIVATION_REASON_OTHER_TEXT_MAX_LENGTH}
              placeholder="Tell us more…"
              className="h-12 w-full rounded-xl border-[1.5px] border-primary px-3.5 text-[15px] text-dark outline-none placeholder:text-grey10"
            />
            <div className="mt-1.5 text-right text-xs text-grey10">
              {reasonText.length} / {DEACTIVATION_REASON_OTHER_TEXT_MAX_LENGTH}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-4 rounded-xl border border-danger/20 bg-dangerLight px-3.5 py-2.5 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="mt-6 flex gap-3">
        <button
          type="button"
          disabled={submitting}
          onClick={handleClose}
          className="h-12 flex-1 rounded-xl border-[1.5px] border-grey bg-white text-[15px] font-semibold text-dark hover:bg-grey4 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!canSubmit || submitting}
          onClick={handleConfirm}
          className="h-12 flex-1 rounded-xl bg-danger text-[15px] font-semibold text-white hover:bg-[#b30000] disabled:cursor-not-allowed disabled:opacity-45"
        >
          {submitting ? 'Setting Inactive…' : 'Set Inactive'}
        </button>
      </div>
    </AppModal>
  );
};

export default DeactivatePoolModal;
